// Billing / tier gating. Legacy: requireTier/checkBatchUsage/design limits
// (snapshot L3722-3767, 9223-9258). Pure tier math + Supabase usage counts.
import { state, type Tier } from './state'
import { effectiveTier, API_BASE } from './auth/org'
import { supabase } from './auth/supabase'
import { BATCH_FREE_LIMIT } from './constants'

const RANK: Record<Tier, number> = { free: 0, pro: 1, business: 2 }

/** True if the effective tier meets `tier`. `onDenied` lets callers show an upgrade modal. */
export function requireTier(tier: Tier, onDenied?: (featureKey?: string) => void, featureKey?: string): boolean {
  if (RANK[effectiveTier()] >= RANK[tier]) return true
  onDenied?.(featureKey)
  return false
}

/** Cloud design cap: free = 10, paid = unlimited (legacy getDesignLimit). */
export function getDesignLimit(): number {
  return effectiveTier() === 'free' ? 10 : Infinity
}

/**
 * Free-tier batch gate: ≤5 batch exports/month (legacy checkBatchUsage).
 * Returns { allowed, remaining }. Anonymous users are not allowed (must sign in).
 * Fails OPEN on query error, matching legacy behaviour.
 */
export async function checkBatchUsage(): Promise<{ allowed: boolean; remaining: number; reason?: string }> {
  const user = state.currentUser
  if (!user) return { allowed: false, remaining: 0, reason: 'signin' }
  if (effectiveTier() !== 'free') return { allowed: true, remaining: Infinity }
  try {
    const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString()
    const { count, error } = await supabase
      .from('export_logs')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .eq('action_type', 'batch')
      .gte('created_at', monthStart)
    if (error) return { allowed: true, remaining: BATCH_FREE_LIMIT } // fail open
    const used = count ?? 0
    return { allowed: used < BATCH_FREE_LIMIT, remaining: Math.max(0, BATCH_FREE_LIMIT - used), reason: used >= BATCH_FREE_LIMIT ? 'limit' : undefined }
  } catch {
    return { allowed: true, remaining: BATCH_FREE_LIMIT } // fail open
  }
}

/**
 * Start a Stripe Checkout session for `plan` and redirect to it (legacy: the
 * ?plan= auto-checkout right after signup, snapshot L8839-8850). Returns the
 * checkout URL (or null) so tests/callers can observe without navigating.
 */
export async function startCheckout(plan: 'pro' | 'business', accessToken: string, navigate = true): Promise<string | null> {
  try {
    const resp = await fetch(`${API_BASE}/api/v1/checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ plan }),
    })
    const result = await resp.json().catch(() => ({}))
    const url = typeof result?.url === 'string' ? result.url : null
    if (url && navigate) window.location.href = url
    return url
  } catch (e) {
    console.error('[billing] checkout failed', e)
    return null
  }
}
